<template>
  <div v-if="(data && data.length) || showEmptyTable">
    <Loader :show="searched" :fixed="false" style="z-index: 5;" />
    <div
      :class="`tab-contents d-flex flex-row${
        reverse ? '-reverse' : ''
      } justify-content-between`"
    >
      <div
        v-if="(data && data.length) || !showEmptyTable"
        :class="expanded ? 'table-container-expanded' : 'table-container'"
        ref="table-parent"
      >
        <div class="list-filter-wrapper">
          <button class="select-all-button" @click="selectOrDeselectAll()">
            {{ buttonText }}
          </button>
          <el-input
            placeholder="Search query"
            v-model="listFilter"
            clearable
          ></el-input>
          <button class="expand-button" @click="expandTable()">
            {{ expanded === true ? 'Collapse' : 'Expand' }}
          </button>
        </div>
        <div class="calculate-confidence-wrapper" ref="table-parent">
          <el-tooltip v-if="!confidence" content="You need to check at least one metric to calculate significance.">
            <button
              disabled
              class="select-all-button"
              @click="calculateConfidence()"
            >
              Calculate
            </button>
          </el-tooltip>
          <button
            v-else
            class="select-all-button"
            @click="calculateConfidence()"
          >
            Calculate
          </button>
          <el-tooltip v-if="!confidence" content="You need to check at least one metric to calculate significance.">
            <el-select
              :disabled="!confidence"
              class="select-calculations"
              v-model="confidenceOption"
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="(option, index) in confidenceCalculateOptions"
                  :key="index"
                  :label="option.label"
                  :value="option.value"
                >
                  <span style="float: left">
                    {{option.label}}
                  </span>
                </el-option>
              </el-option-group>
            </el-select>
          </el-tooltip>
          <el-select
              v-else
              class="select-calculations"
              v-model="confidenceOption"
            >
              <el-option-group label="Press Tab/Escape to exit">
                <el-option
                  v-for="(option, index) in confidenceCalculateOptions"
                  :key="index"
                  :label="option.label"
                  :value="option.value"
                >
                  <span style="float: left">
                    {{option.label}}
                  </span>
                </el-option>
              </el-option-group>
            </el-select>
        </div>
        <div class="fragrances-container">
          <div class="fragrances-wrapper" :style="fragrancesWrapperStyle">
            <div class="d-flex align-items-center">
              <div class="tag-group" style="margin-top: 10px; margin-bottom: 10px;" v-if="confidence">
                <el-badge
                  v-for="(filter, index) in confidenceFilters"
                  :value="getBadgeValue(filter.value)"
                  :key="index"
                  type="info"
                  :style="{'cursor': 'pointer','margin-right': '20px', 'color': filter.color}"
                >
                  <el-tag
                    :key="index"
                    effect="dark"
                    :style="{ 'background-color': filter.color, border: 0 }"
                    v-model="confidenceFilter"
                    :value="filter.value"
                    @click="setConfidenceFilter(filter.value)"
                  >
                    <p style="color: white">
                      {{ filter.label }}
                    </p>
                  </el-tag>
                </el-badge>
              </div>
            </div>
            <div
              class="
                data data-hide data-header
                d-flex
                flex-row
                justify-content-between
                align-items-center
              "
            >
              <div class="d-flex align-items-center fragrance-name">
                {{ searchType === "concepts" ? "Concept" : "Fragrance" }} name
              </div>
              <div
                class="
                  attribute-values
                  d-flex
                  justify-content-between
                  align-items-center
                "
              >
                <div
                  class="metric-value"
                  v-for="metric in metricTypes"
                  :ref="`${metric.value}-${metric.aggregation}`"
                  :key="`${metric.value}-${metric.aggregation}`"
                  v-b-tooltip:hover.html="
                    `${formatChartLabel(metric.value)}<br>${formatChartLabel(
                      metric.aggregation
                    )}`
                  "
                >
                  {{
                    `[${parseAggregationType(
                      metric.aggregation
                    )}] ${formatChartLabel(metric.value)}`
                  }}
                </div>
              </div>
            </div>
            <RecycleScroller
              :key="`study-table-${forceRerender}`"
              class="scroller"
              :items="filteredData"
              :item-size="40"
              keyField="id"
              :emit-update="true"
              v-slot="{ item, index }"
            >
              <div
                class="
                  data data-hide
                  d-flex
                  flex-row
                  justify-content-between
                  align-items-center
                "
              >
                <div
                  class="d-flex align-items-start fragrance-name"
                  v-if="item.prod"
                >
                  <b-tooltip
                    class="checkbox-tooltip"
                    :target="`checkbox-tooltip-${fragUniqueId(item)}`"
                    placement="right"
                  >
                    {{ formatValue(item.c_mi_number) }}
                    <br />
                    {{ formatValue(item.category) }}
                    <br />
                    {{ item.name || item.prod }}
                  </b-tooltip>
                  <Checkbox
                    :color="'purple'"
                    style="width: 100%"
                    :id="`checkbox-tooltip-${fragUniqueId(item)}`"
                    :name="fragUniqueId(item)"
                    :selected="selfFilters.selectedProducts"
                    :value="selfFilters.selectedProducts[fragUniqueId(item)]"
                    :link="true"
                    @change="(val) => (selectOne(fragUniqueId(item), val))"
                  >
                    <span>{{ item.name || item.prod }}</span>
                  </Checkbox>
                  <router-link
                    style="margin-left: 5px;"
                    :to="{
                      name:
                        searchType === 'concepts'
                          ? 'concept-profile'
                          : 'fragrance-profile',
                      params: { id: item.study_id, code: item.prod },
                      query: {
                        attributes: JSON.stringify(
                          metricTypes.map((el) => el.value)
                        )
                      },
                    }"
                    target="_blank"
                  >
                    <font-awesome-icon icon="external-link-alt" />
                  </router-link>
                </div>
                <span v-else>{{ formatValue(item.product) }}</span>
                <div
                  class="
                    attribute-values
                    d-flex
                    justify-content-between
                    align-items-center
                  "
                >
                  <div
                    class="
                      metric-value
                      d-flex
                      justify-content-center
                      align-items-center
                    "
                    :style="getWidth(metric)"
                    v-for="metric in metricTypes"
                    :key="`${metric.value}–${metric.aggregation}-${index}`"
                  >
                    {{ getMetricValue(item, metric) }}
                    <el-tag
                        effect="plain"
                        type="success"
                        :style="{ color: getConfidenceColor(getConfidenceValue(item, metric)), border: 0 }"
                        v-if="checkConfidenceDisplay(item, metric)"
                    >
                      <i :class="getConfidenceIcon(getConfidenceValue(item, metric))"></i>
                      {{ parseConfidenceValue(getConfidenceValue(item, metric)) }}
                    </el-tag>
                  </div>
                </div>
              </div>
            </RecycleScroller>
          </div>
        </div>
      </div>
      <div
        v-else
        :class="`tab-contents d-flex flex-row${
          reverse ? '-reverse' : ''
        } justify-content-between`"
      >
        <span
          class="data data-header align-items-center"
        >
          No data found after filtering. Please change filters and try again.
        </span>
      </div>
      <div :class="!expanded ? 'results-box' : 'results-box-collapsed'" v-if="!expanded">
        <div
          class="d-flex flex-column align-items-center justify-content-center"
          style="height: 25%"
        >
          <div class="row w-100">
            <template
              v-for="(item, index) in Object.keys(filterValues.demographics)
                .map((k) => filterValues.demographics[k])
                .filter((e) => !e.label.toLowerCase().includes('age'))"
            >
              <div
                class="form-input d-flex flex-column align-items-center"
                :key="`input-${index}`"
              >
                <div class="input-wrapper">
                  <label style="margin: 0">{{ item.label }}</label>
                  <el-select
                    class="select-group-item"
                    v-model="selfFilters[item.key]"
                    :placeholder="`Select ${item.label}`"
                    clearable
                    :disabled="item.label === 'BUMO'"
                    filterable
                    collapse-tags
                  >
                    <el-option-group label="Press Tab/Escape to exit">
                      <el-option
                        v-for="option in filterFields.demographics[item.key]"
                        :key="option.label || option"
                        :label="option.label || option"
                        :value="option.label || option"
                      >
                        <span style="float: left">{{
                          option.label || option
                        }}</span>
                      </el-option>
                    </el-option-group>
                  </el-select>
                </div>
              </div>
            </template>
          </div>
          <div class="w-100 d-flex justify-content-between">
            <div
              class="input-wrapper centered w-50"
              style="margin: 15px 25px 0"
            >
              <div>
                <label style="margin: 0 10px 10px">Age</label>
                <el-switch
                  v-model="selfFilters.year.disabled"
                  active-color="#2a068b"
                  inactive-color="#e8eaea"
                />
              </div>
              <el-slider
                v-if="selfFilters.year.disabled"
                v-model="selfFilters.year.range"
                range
                :min="yearObject['min']"
                :max="yearObject['max']"
                :marks="yearObject['marks']"
                @change="parseYears()"
                show-stops
              >
              </el-slider>
            </div>
            <div class="button-wrapper justify-content-end">
              <el-tooltip
                content="Please select at least one fragrance to perform filtering."
                placement="top"
                effect="dark"
                :disabled="hasSomeFragrancesSelected()"
              >
                <span>
                  <button
                    class="btn filter-button"
                    @click="filter"
                    :disabled="!hasSomeFragrancesSelected()"
                  >
                  filter
                  </button>
                </span>
              </el-tooltip>
            </div>
          </div>
        </div>

        <div class="results-header">
          <div class="results-header__title">Analytics Results</div>
          <img
            src="../../../assets/images/icons/excel-export-H.svg"
            class="dwn-button"
            @click="download('metrics', chosenXlsxFilters)"
          />
          <el-select
            class="select-group-item"
            :placeholder="`Filter results`"
            filterable
            collapse-tags
            multiple
            v-model="chosenXlsxFilters"
          >
            <el-option-group label="Press Tab/Escape to exit">
              <el-option
                v-for="option in xlsxFilters"
                :key="option.value"
                :value="option.value"
                :label="option.label"
              >
                <span style="float: left">
                  {{option.label}}
                </span>
              </el-option>
            </el-option-group>
          </el-select>
        </div>

        <div class="charts-container d-flex flex-column align-items-center">
          <template v-if="summary && Object.keys(summary) && hasRDF && !isIRT">
            <HorizontalAnalyticsChart
              v-for="(item, idx) in chartData"
              :key="`chart-${idx}`"
              :style="`order: ${getChartOrder(idx)}`"
              :data="item.data"
              :mean="item.mean"
              :n-values="item.counts"
            ></HorizontalAnalyticsChart>
          </template>

          <template v-if="summary && Object.keys(summary) && hasGenisys && !isIRT">
            <BarChart
              v-for="(item, idx) in genisysChartData"
              :key="`chart-${idx}`"
              :chart-data="item"
              :options="genisysChart.options"
              :styles="{height: '300px', width: '100%', position: 'relative'}"
              :data-types="{ Count: 'value' }"
            ></BarChart>
          </template>
        </div>

      </div>
    </div>
  </div>
  <div v-else>
    <div class="no-data d-flex flex-column align-items-center">
      <div
        v-if="ready.metrics"
        class="h-100 d-flex justify-content-center align-items-center"
      >
        <i>No data</i>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { mapActions, mapGetters } from 'vuex'
import generalActions from '../../../store/modules/general/actionTypes'
import studiesActions from '../../../store/modules/studies/actionTypes'
import studiesGetters from '../../../store/modules/studies/getterTypes'
import tooltips from '../../../constants/tooltips'
import _ from 'lodash'
import HorizontalAnalyticsChart from '../../shared/HorizontalAnalyticsChart'
import BarChart from '../../shared/BarChart'
import Checkbox from '../../shared/Checkbox'
import searchFunctions from '../../../helpers/searchFunctions'
import Loader from '../../shared/Loader'

export default {
  name: 'FragranceMetricsTable',
  components: { Loader, Checkbox, HorizontalAnalyticsChart, BarChart },
  props: {
    data: {
      type: Array,
      required: false
    },
    summary: {
      type: Object,
      required: false
    },
    fileType: String,
    searchType: String,
    filterFields: Object,
    download: Function,
    ready: Object,
    reverse: Boolean,
    fields: Array,
    isGenisys: Boolean
  },
  data () {
    return {
      resizeToggle: false,
      showEmptyTable: false,
      forceRerender: 0,
      buttonText: 'Deselect all',
      sortBy: {
        key: 'category',
        desc: false
      },
      selfFilters: {
        year: {
          disabled: true,
          range: []
        },
        bumo: '',
        selectedProducts: {},
        ethnicity: '',
        social_class: '',
        gender: '',
        isFiltered: false
      },
      xlsxFilters: [],
      chosenXlsxFilters: [],
      pagination: {
        page: 1,
        perPage: 20
      },
      listFilter: '',
      fileQuery: '',
      searched: false,
      intervals: {
        metrics: '',
        significance: ''
      },
      tooltips,
      confidenceFilters: [
        { type: 'info', label: 'All', value: undefined, color: 'grey' },
        { type: 'success', label: '99 %', value: { level: 0.01, table: 'mt' }, color: '#67c23a' },
        { type: '', label: '95 %', value: { level: 0.05, table: 'mt' }, color: '#95d475' },
        { type: 'danger', label: '90 %', value: { level: 0.1, table: 'mt' }, color: '#c2e7b0' },
        { type: 'danger', label: '90 %', value: { level: 0.1, table: 'let' }, color: '#fbc4c4' },
        { type: '', label: '95 %', value: { level: 0.05, table: 'let' }, color: '#f89898' },
        { type: 'success', label: '99 %', value: { level: 0.01, table: 'let' }, color: '#f56c6c' }
      ],
      confidenceExamples: [
        { value: 99, color: '#67c23a' },
        { value: 95, color: '#95d475' },
        { value: 90, color: '#c2e7b0' },
        { value: 99, color: '#f56c6c' },
        { value: 95, color: '#f89898' },
        { value: 90, color: '#fbc4c4' }
      ],
      confidenceCalculateOptions: [
        { value: 'avg', label: 'Selection vs Average' },
        { value: 'rest', label: 'Selection vs Average of the rest' }
      ],
      confidenceFilter: undefined,
      confidenceOption: 'avg',
      confidenceValue: 0,
      expanded: false,
      chart: {
        options: {
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false
                }
              }
            ],
            yAxes: [
              {
                ticks: {
                  min: 0
                }
              }
            ]
          },
          barValueSpacing: 20,
          responsive: true,
          maintainAspectRatio: false
        }
      },
      genisysChart: {
        options: {
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false
                }
              }
            ],
            yAxes: [
              {
                ticks: {
                  min: 0,
                  callback: function (val) {
                    return val % 1 === 0 ? val : null
                  }
                }
              }
            ]
          },
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                let label = data.datasets[tooltipItem.datasetIndex].label || ''
                // eslint-disable-next-line standard/computed-property-even-spacing
                let value = Number(data.datasets[tooltipItem.datasetIndex].data[
                  tooltipItem.index
                ])
                if (isNaN(value)) {
                  value = 0
                }
                label = label.split('.')[0]
                return label + ': ' + `${value.toFixed()}`
              },
              title: function (tooltipItem, data) {
                let label = data.labels[tooltipItem[0].index]
                let nextLabel = data.labels[tooltipItem[0].index + 1]
                if (nextLabel) {
                  label += ' - ' + nextLabel
                } else {
                  const step = Number(data.labels[1]) - Number(data.labels[0])
                  label += ' - ' + (Number(label) + step).toFixed(2)
                }
                return label
              }
            }
          },
          responsive: true,
          maintainAspectRatio: false
        }
      },
      yearObject: {
        min: 18,
        max: 64,
        marks: { 18: '18', 64: '64' }
      }
    }
  },
  computed: {
    ...mapGetters({
      filterValues: studiesGetters.GET_SEARCH_FILTERS,
      metricsJobId: studiesGetters.GET_METRICS_JOB_ID,
      significanceJobId: studiesGetters.GET_SIGNIFICANCE_JOB_ID,
      significanceResults: studiesGetters.GET_SIGNIFICANCE_RESULTS
    }),
    filteredData () {
      let arrayCopy = []
      try {
        arrayCopy = this.formatData()
      } catch (error) {
        return []
      }
      if (this.listFilter) {
        arrayCopy = arrayCopy.filter((el) =>
          el.name
            ? el.name.toLowerCase().includes(this.listFilter.toLowerCase())
            : el.prod.toString().includes(this.listFilter)
        )
      }

      if (this.confidenceFilter !== undefined && this.confidence) {
        arrayCopy = arrayCopy.filter(el => {
          return this.metricTypes.some(metric => {
            const cEl = this.getConfidenceValue(el, metric)
            return cEl !== undefined &&
                cEl.value.level !== undefined &&
                cEl.value.table !== undefined &&
                cEl.value.level === this.confidenceFilter.level &&
                cEl.value.table === this.confidenceFilter.table
          })
        })
      }

      return arrayCopy
    },
    paginatedData () {
      const { perPage, page } = this.pagination
      return this.filteredData.slice((page - 1) * perPage, page * perPage)
    },
    chartData () {
      let summ = _.cloneDeep(this.summary)
      let metrics = []
      this.metricTypes.filter(metric => metric.value.includes('_')).forEach(metric => {
        metrics.push(metric.value)
      })
      metrics = metrics.concat(['liking', 'strength', 'purchase_intention'])
      Object.keys(summ)
        .filter(key => !metrics.includes(key))
        .forEach(key => delete summ[key])
      let parsedData = {}
      Object.keys(summ).forEach((el) => {
        if (Object.keys(summ[el]).length) {
          parsedData[el] = Object.keys(summ[el]).map((key) => {
            return { key, value: summ[el][key] }
          })
        }
      })
      let chartData = {}
      Object.keys(parsedData).forEach((el) => {
        const mean = parsedData[el].find((val) => val.key === 'mean').value
        const filteredData = parsedData[el]
          .filter(
            (val) =>
              ['top_2_box', 'just_right', 'bottom_2_box'].includes(val.key) &&
              !val.key.includes('.')
          )
          .reverse()
        const counts = parsedData[el].filter((el) => el.key.includes('.'))
        const labels = filteredData.map((val) =>
          this.formatChartLabel(val.key)
        )
        if (el !== 'strength') {
          labels[labels.indexOf('Just right')] = 'Neutral'
        }
        const data = filteredData.map((val) => val.value)
        chartData[el] = {
          mean,
          counts,
          data: {
            labels,
            datasets: [
              {
                label: this.formatChartLabel(el),
                backgroundColor: 'rgba(163,160,251,0.5)',
                data
              }
            ]
          }
        }
      })
      return chartData
    },
    genisysChartData () {
      const metrics = this.metricTypes.filter((el) =>
        ['hfd_hedonic',
          'addiction',
          'cogproc',
          'attention',
          'relaxation',
          'energizing'].includes(el.value)
      ).map((el) => el.value + '.' + el.aggregation)
      const data = metrics.map((metric) => {
        const values = this.filteredData.map((el) => el[metric])
          .filter((val) => val !== null)
          .sort((a, b) => a - b)
        return { metric, values }
      })
      const divisions = data.map((el) => {
        const min = Math.min(...el.values)
        const max = Math.max(...el.values)
        const range = max - min
        const step = range / 8.0
        const divisions = []
        for (let i = 0; i < 8; i++) {
          divisions.push(min + step * i)
        }
        return {
          metric: el.metric,
          step: step,
          divisions
        }
      })
      const counts = divisions.map((el) => {
        const values = el.divisions.map((div) => {
          let count = data.find((val) => val.metric === el.metric).values.filter(
            (val) => val >= div && val <= div + el.step
          ).length
          if (div === divisions[divisions.length - 1]) { count = count + 1 }
          return { div, count }
        })
        return { metric: el.metric, values }
      })
      const chartData = {}
      counts.forEach((el) => {
        const labels = el.values.map((val) => val.div.toFixed(2))
        const data = el.values.map((val) => val.count)
        chartData[el.metric] = {
          labels,
          datasets: [
            {
              label: this.formatChartLabel(el.metric),
              backgroundColor: 'rgba(163,160,251,0.5)',
              data
            }
          ]
        }
      })
      return chartData
    },
    fragrancesWrapperStyle () {
      const fragNameWidth = window.innerWidth > 1400 ? 300 : 220
      const parentElement = this.$refs['table-parent']
      const calculatedWidth =
        fragNameWidth + 150 * this.metricTypes.length + 20
      return {
        minWidth: `${
          parentElement
            ? parentElement.clientWidth > calculatedWidth
              ? parentElement.clientWidth + 'px'
              : calculatedWidth + 'px'
            : '100%'
        }`,
        maxWidth: `${
          parentElement
            ? parentElement.clientWidth > calculatedWidth
              ? parentElement.clientWidth + 'px'
              : calculatedWidth + 'px'
            : '100%'
        }`
      }
    },
    metricTypes () {
      return this.filterValues.metrics.metricsList.length
        ? this.filterValues.metrics.metricsList.map((el) => {
          const valueSplit = el.metric.value.split('_')
          return {
            value:
                valueSplit.length > 1
                  ? valueSplit.join('_')
                  : el.metric.value,
            aggregation: el.aggregation.value,
            confidence: el.confidence.value
          }
        })
        : []
    },
    hasGenisys () {
      return !!this.metricTypes.find((el) =>
        ['hfd_hedonic',
          'addiction',
          'cogproc',
          'attention',
          'relaxation',
          'energizing'].includes(el.value)
      )
    },
    hasRDF () {
      return !!this.metricTypes.find((el) =>
        ['liking',
          'strength',
          'purchase_intention'].includes(el.value) || el.value.includes('_')
      )
    },
    confidence () {
      return this.metricTypes.some(metric => metric.confidence)
    },
    isIRT () {
      console.log('FragranceMetricsTable.vue')
      console.log(this)
      const irtMetrics = ['irt_yes', 'irt_no', 'hi_yes', 'hi_no']
      const containsIRTAgg = (element) => irtMetrics.includes(element.aggregation)
      return this.metricTypes.some(containsIRTAgg)
    }
  },
  methods: {
    ...mapActions({
      getStudyFiles: studiesActions.FETCH_FILES,
      queueAnalyticSearch: studiesActions.QUEUE_METRIC_SEARCH,
      getMetrics: studiesActions.CHECK_METRIC_JOB_STATUS,
      fetchXlsxFilters: studiesActions.FETCH_FRAGRANCE_XLSX_FILTERS,
      queueSignificanceCalculation: studiesActions.QUEUE_SIGNIFICANCE_CALCULATION,
      getSignificance: studiesActions.CHECK_SIGNIFICANCE_JOB_STATUS,
      toggleLoading: generalActions.TOGGLE_LOADING
    }),
    fragUniqueId (frag) {
      return `${frag.study_id || frag.id}-${frag.c_mi_number}-${frag.prod}`
    },
    sortByMultipleKeys (keys) {
      return (a, b) => {
        if (keys.length === 0) return 0
        const key = Object.keys(a).find((el) => el.includes(keys[0]))
        let firstVal =
          typeof a[key] !== 'number'
            ? a[key] && a[key].includes('%')
              ? Math.round(parseInt(a[key].slice(0, -1)))
              : 0
            : Math.round(a[key])
        let secondVal =
          typeof b[key] !== 'number'
            ? b[key] && b[key].includes('%')
              ? Math.round(parseInt(b[key].slice(0, -1)))
              : 0
            : Math.round(b[key])
        if (firstVal < secondVal) return 1
        else if (firstVal > secondVal) return -1
        else return this.sortByMultipleKeys(keys.slice(1))(a, b)
      }
    },
    parseAdditionalData () {
      let social = {}
      if (this.selfFilters.social_class !== '') {
        if (this.filterFields.demographics) {
          social = this.filterFields.demographics.social_class.find(
            (el) => el.label === this.selfFilters.social_class
          )
        }
      }

      let ethnicity = {}
      if (this.selfFilters.ethnicity !== '') {
        ethnicity = {
          field: 'ethnicity',
          filter: {
            type: 'equal',
            value: this.selfFilters.ethnicity
          }
        }
      }

      let gender = {}
      if (this.selfFilters.gender !== '') {
        gender = {
          field: 'gender',
          filter: {
            type: 'equal',
            value: this.selfFilters.gender
          }
        }
      }

      let age = {}
      if (this.selfFilters.year.disabled) {
        age = {
          field: 'age',
          filter: {
            type: 'range',
            from: this.selfFilters.year.range[0],
            to: this.selfFilters.year.range[this.selfFilters.year.range.length - 1]
          }
        }
      }
      // filter selected products from list
      let fragrances = []
      const hasFiltersSet =
        this.selfFilters.gender ||
        this.selfFilters.bumo ||
        this.selfFilters.social_class ||
        this.selfFilters.ethnicity

      if (hasFiltersSet) {
        fragrances = this.data
          .map((fragrance) => ({
            prod: fragrance.prod,
            c_mi_number: fragrance.c_mi_number,
            id: fragrance.id
          }))
          .filter((fragrance) => {
            return (
              fragrance.prod &&
              this.selfFilters.selectedProducts[this.fragUniqueId(fragrance)]
            )
          })
      }

      return {
        fragrances: fragrances,
        social: social,
        gender: gender,
        age: age,
        ethnicity: ethnicity
      }
    },
    parseDemographicsFragrances (data) {
      const values = {
        demographics_fragrances: []
      }
      const parseRangeData = (lowerValue, higherValue, field, dataObj) => {
        if (lowerValue.value || higherValue.value) {
          const parsedRange = {
            field: field,
            filter: {
              type: 'range',
              from: lowerValue.value,
              to: higherValue.value
            }
          }
          dataObj.push(parsedRange)
        }
      }
      const parseEqualData = (query, dataObj, excludedTags) => {
        Object.keys(query).forEach((tagKey) => {
          if (!excludedTags.includes(tagKey) && query[tagKey].value) {
            if (
              (Array.isArray(query[tagKey].value) &&
                query[tagKey].value.length) ||
              (typeof query[tagKey].value === 'string' &&
                query[tagKey].value.length)
            ) {
              dataObj.push({
                field: query[tagKey].key,
                filter: {
                  type: 'equal',
                  value: query[tagKey].value
                }
              })
            } else if (
              typeof query[tagKey].value === 'object' &&
              Object.keys(query[tagKey].value).length
            ) {
              dataObj.push(query[tagKey].value)
            }
          }
        })
      }
      const demographicsQuery = { ...data }
      parseRangeData(
        demographicsQuery.startAge,
        demographicsQuery.endAge,
        'age',
        values.demographics_fragrances
      )
      parseEqualData(demographicsQuery, values.demographics_fragrances, [
        'startAge',
        'endAge'
      ])
      return values.demographics_fragrances
    },
    async filter () {
      this.searched = true
      const additionalData = this.parseAdditionalData()
      const queryFilters = _.cloneDeep(this.filterValues)
      const demographicsFragrances = {
        bumo: { label: 'BUMO', key: 'bumo', value: '' },
        socialClass: {
          label: 'Social Class',
          key: 'social_class',
          value: additionalData.social.filter
            ? additionalData.social.filter.value
            : ''
        },
        gender: {
          label: 'Gender',
          key: 'gender',
          value: additionalData.gender.filter
            ? additionalData.gender.filter.value
            : ''
        },
        ethnicity: {
          label: 'Ethnicity',
          key: 'ethnicity',
          value: additionalData.ethnicity.filter
            ? additionalData.ethnicity.filter.value
            : ''
        },
        startAge: {
          label: 'Lower age limit',
          key: 'age',
          value: additionalData.age.filter
            ? additionalData.age.filter.from
            : ''
        },
        endAge: {
          label: 'Higher age limit',
          key: 'age',
          value: additionalData.age.filter ? additionalData.age.filter.to : ''
        }
      }
      let selectedFragrancesId = Object.keys(this.selfFilters.selectedProducts).filter(
        key => this.selfFilters.selectedProducts[key] === true
      )
      let selectedFragrances = this.data.filter(ob => selectedFragrancesId.includes(
        this.fragUniqueId(ob)
      )).map(ob => ({
        id: ob.id,
        c_mi_number: ob.c_mi_number,
        prod: ob.prod
      })) || []
      await this.queueAnalyticSearch({
        q: {
          ...searchFunctions.parseDataForSearch(queryFilters),
          demographics_fragrances: this.parseDemographicsFragrances(
            demographicsFragrances
          ),
          fragrances: selectedFragrances
        },
        data: additionalData.fragrances.length
          ? additionalData.fragrances
          : null
      })

      this.selfFilters.isFiltered = true

      this.intervals.metrics = setInterval(async () => {
        const data = await this.getMetrics(this.metricsJobId)
        if (data) {
          clearInterval(this.intervals.metrics)
          this.searched = false
        }
      }, 3000)
    },
    formatData () {
      const formattedArray = []
      this.data.forEach((el, idx) => {
        formattedArray.push({
          ...el,
          study_id: el.id,
          id: idx
        })
      })
      return formattedArray
    },
    formatValue (value) {
      if (!value || value === 'No Tag') {
        return '-'
      } else if (typeof value === 'string') {
        return value && value.charAt(0).toUpperCase() + value.slice(1)
      } else {
        return value
      }
    },
    formatChartLabel (value) {
      if (this.isGenisys) {
        value = value.replace('.mean', '')
      }
      if (value.includes('hfd_')) {
        value = value.replace('hfd_', '')
        return value.charAt(0).toUpperCase() + value.slice(1)
      } else if (value === 'cogproc') {
        return 'Cognitive Processing'
      } else {
        return value.charAt(0).toUpperCase() + value.slice(1).replace(/_+/g, ' ')
      }
    },
    changeSort (newKey) {
      if (this.sortBy.key === newKey) {
        const value = !this.sortBy.desc
        this.$set(this.sortBy, 'desc', value)
      } else {
        this.$set(this.sortBy, 'key', newKey)
        this.$set(this.sortBy, 'desc', false)
      }
    },
    hasSomeFragrancesSelected () {
      return Object.keys(this.selfFilters.selectedProducts).length && Object.values(this.selfFilters.selectedProducts).some(
        e => e === true
      )
    },
    setFragrancesButtonText () {
      this.buttonText = this.hasSomeFragrancesSelected() ? 'Deselect all' : 'Select all'
    },
    selectOne (uniqueId, val) {
      this.selfFilters.selectedProducts[uniqueId] = val
      this.setFragrancesButtonText()
    },
    selectOrDeselectAll () {
      if (this.hasSomeFragrancesSelected()) {
        this.data.forEach(fragrance => {
          this.selfFilters.selectedProducts[this.fragUniqueId(fragrance)] = false
        })
      } else {
        this.data.forEach(fragrance => {
          this.selfFilters.selectedProducts[this.fragUniqueId(fragrance)] = true
        })
      }
      this.setFragrancesButtonText()
      this.forceRerender += 1
    },
    getMetricValue (obj, metric) {
      const key = Object.keys(obj).find(
        (el) => el.includes(metric.value) && el.includes(metric.aggregation)
      )
      if (key) {
        const metricFilter = key.split('.')
        if (metricFilter[1] === 'mean') {
          return obj[key] ? obj[key].toFixed(2) : '-'
        } else {
          return obj[key] ? `${Math.round(obj[key])}%` : '-'
        }
      } else {
        return '-'
      }
    },
    getChartOrder (tag) {
      switch (tag) {
        case 'liking':
          return 2
        case 'strength':
          return 4
        case 'purchase_intention':
          return 3
        default:
          return 1
      }
    },
    getMetricLabel (metric) {
      const metricRange = this.filterFields.metrics_range
      const metricFilter = metric.split('.')
      if (metricFilter[1] === 'mean') {
        const range = `[${metricRange[metricFilter[0]][0]}-${
          metricRange[metricFilter[0]][1]
        }]`
        return `${metricFilter[0]}, ${metricFilter[1]} ${range}`.replace(
          /_+/g,
          ' '
        )
      }
      return `${metricFilter[0]}, ${metricFilter[1]} [%]`.replace(/_+/g, ' ')
    },
    parseAggregationType (type) {
      switch (type) {
        case 'top_box':
          return 'TB'
        case 'top_2_box':
          return 'T2B'
        case 'mean':
          return 'M'
        case 'bottom_box':
          return 'BB'
        case 'bottom_2_box':
          return 'B2B'
        case 'just_right':
          return 'JAR/N'
        case 'variance':
          return 'VAR'
        case 'hi_yes':
          return 'HiY'
        case 'hi_no':
          return 'HiN'
        case 'irt_yes':
          return 'YES'
        case 'irt_no':
          return 'NO'
        default:
          return ''
      }
    },
    parseYears () {
      // this.searchFilters.essentials.startYear.value = this.formData.year[0]
      // this.searchFilters.essentials.endYear.value = this.formData.year[1]
    },
    nextConfidence () {
      this.confidenceValue = (this.confidenceValue + 1) % 6
    },
    expandTable () {
      this.expanded = !this.expanded
    },
    getWidth (metric) {
      const metricRef = `${metric.value}-${metric.aggregation}`
      const width = `width: ${this.$refs[metricRef][0].clientWidth}px`
      return width
    },
    async calculateConfidence () {
      this.toggleLoading(true)
      const q = {
        calculation: this.confidenceOption,
        metrics: this.metricTypes.map(metric => {
          return {
            field: metric.value,
            aggregation: metric.aggregation,
            confidence: metric.confidence
          }
        })
      }
      const selected = this.selfFilters.selectedProducts
      const dataResults = this.data
      await this.queueSignificanceCalculation({
        q: q,
        data: {
          selected: selected,
          data: dataResults
        }
      })

      this.intervals.significance = setInterval(async () => {
        const status = await this.getSignificance({ jobId: this.significanceJobId })
        if (status) {
          console.log('status was true')
          clearInterval(this.intervals.significance)
          this.toggleLoading(false)
          this.searched = false
        }
      }, 5000)
    },
    getConfidenceValue (item, metric) {
      const itemid = this.fragUniqueId(item)
      const metricName = metric.value + '.' + metric.aggregation + '.confidence'
      const result = this.significanceResults.find(
        e => e.id === itemid
      )
      if (result) {
        return {
          id: result.id,
          value: result[metricName] ? result[metricName] : { level: undefined, value: undefined }
        }
      } else {
        return undefined
      }
    },
    getConfidenceColor (result) {
      if (result !== undefined) {
        if (result.value === '-') {
          return '#ccc'
        } else if (result.value.level === 0.01 && result.value.table === 'mt') {
          return '#67c23a'
        } else if (result.value.level === 0.05 && result.value.table === 'mt') {
          return '#95d475'
        } else if (result.value.level === 0.1 && result.value.table === 'mt') {
          return '#c2e7b0'
        } else if (result.value.level === 0.01 && result.value.table === 'let') {
          return '#f56c6c'
        } else if (result.value.level === 0.05 && result.value.table === 'let') {
          return '#f89898'
        } else if (result.value.level === 0.1 && result.value.table === 'let') {
          return '#fbc4c4'
        } else {
          return '#ccc'
        }
      } else {
        return '#ccc'
      }
    },
    getConfidenceIcon (result) {
      if (result !== undefined) {
        if (result.value === '-' || (result.value.level === undefined && result.value.table === undefined) || (result.value.level === '' && result.value.table === '')) {
          return 'el-icon-remove'
        } else {
          return 'el-icon-success'
        }
      }
    },
    parseConfidenceValue (result) {
      if (result !== undefined) {
        if (result.value.level === 0.01) {
          return 99
        } else if (result.value.level === 0.05) {
          return 95
        } else if (result.value.level === 0.1) {
          return 90
        }
      } else {
        return '-'
      }
    },
    setConfidenceFilter (value) {
      this.confidenceFilter = value
    },
    getBadgeValue (value) {
      const data = this.formatData().filter(el =>
        el.name
          ? el.name.toLowerCase().includes(this.listFilter.toLowerCase())
          : el.prod.toString().includes(this.listFilter)
      )
      return value !== undefined ? data.filter(el => {
        return this.metricTypes.some(metric => {
          const cEl = this.getConfidenceValue(el, metric)
          return cEl !== undefined &&
              cEl.value.level !== undefined &&
              cEl.value.table !== undefined &&
              cEl.value.level === value.level &&
              cEl.value.table === value.table
        })
      }).length : data.length
    },
    reloadConfidence () {
      const initialSignificanceInterval = setInterval(async () => {
        const metricsLength = this.filteredData.length
        if (metricsLength > 0) {
          await this.calculateConfidence()
          clearInterval(initialSignificanceInterval)
        }
      }, 1000)
    },
    checkConfidenceDisplay (item, metric) {
      if (this.getConfidenceValue(item, metric) !== undefined) {
        return this.getConfidenceValue(item, metric).value.level !== '' &&
          this.getConfidenceValue(item, metric).value.value !== '' &&
          this.getMetricValue(item, metric) !== '-' &&
          metric.confidence === true
      } else {
        return false
      }
    }
  },
  async mounted () {
    const minYear = this.yearObject['min']
    const maxYear = this.yearObject['max']
    this.selfFilters.year.range = [minYear, maxYear]
    const xlsxFiltersResponse = await this.fetchXlsxFilters()
    this.xlsxFilters = xlsxFiltersResponse['xlsxFilters']
    this.chosenXlsxFilters = xlsxFiltersResponse['chosenXlsxFilters']
    if (this.metricTypes.some(metric => metric.confidence)) {
      this.reloadConfidence()
    }
    this.setFragrancesButtonText()
  },
  watch: {
    data (newData) {
      if (newData && !this.selfFilters.isFiltered) {
        this.selfFilters.selectedProducts = {}
        newData.forEach(fragrance => {
          this.selfFilters.selectedProducts[this.fragUniqueId(fragrance)] = true
        })
        this.showEmptyTable = false
      } else if (newData && this.selfFilters.isFiltered) {
        this.showEmptyTable = true
      } else if (!newData || (newData && !Object.keys(newData).length)) {
        this.selfFilters.isFiltered = false
      }
      this.setFragrancesButtonText()
    },
    metricTypes () {
      if (this.metricTypes.some(metric => metric.confidence)) {
        this.reloadConfidence()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/variables";

.table-container {
  position: relative;
  width: 50%;
  height: 1200px;
}

.table-container-expanded {
  position: relative;
  width: 100%;
  height: 1200px;
}

.select-group-item {
  margin: 0 !important;
  min-width: unset;
}

.metric-value {
  text-align: center;
  width: auto;
  min-width: 100px;
  padding: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.main-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 40px;
  width: 100%;
  cursor: pointer;
}

.additional-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  overflow: hidden;
  width: 98%;
  cursor: pointer;
}

.search-input {
  position: absolute;
  right: 0;
}

.search-input:hover .input-group {
  width: 230px;
}

.search-input .input-group {
  transition: width 0.35s ease-in;
  flex-wrap: nowrap;
  overflow: hidden;
  width: 39px;
}

.no-data-input {
  position: absolute;
  right: 0;
}

.form-input {
  padding: 3px 10px;
  flex: 0 0 50%;
  max-width: 50%;
  @media screen and (min-width: $break-xl) {
    flex: 0 0 50%;
    max-width: 50%;
  }
  @media screen and (min-width: $break-xxl) {
    flex: 0 0 25%;
    max-width: 25%;
  }
}

.no-data-input:hover .input-group {
  width: 230px;
}

.no-data-input .input-group {
  transition: width 0.35s ease-in;
  flex-wrap: nowrap;
  overflow: hidden;
  width: 39px;
}

.data {
  border-bottom: 1px solid #d1d4d7;
  transition: 0.1s background-color linear;
  padding: 0 10px;
}

.data:hover {
  background-color: #f6f6f6;
}

.additional-data-hide {
  height: 0;
}

.additional-data-show {
  height: 50px;
  border-top: 1px dashed #d1d4d7;
}

.data-show {
  height: 130px;
}

.data-hide {
  height: 40px;
}

.scroller {
  overflow-x: visible;
  overflow-y: unset;
  height: calc(100% - 40px);
}

.tab-contents {
  margin-top: 5px;
  max-height: 1200px;
}

.column-header {
  text-align: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  transition: 0.1s background-color linear;
}

.table-header {
  z-index: 3;
  position: sticky;
  top: 0;
  background-color: white;
  border-bottom: 1px solid #d1d4d7;
}

.column-header:hover {
  background-color: #f6f6f6;
}

.column-body {
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}

.file-data {
  width: 100%;
}

.file-data:hover {
  color: #20a8d8;
}

.file-icon {
  font-size: 1.5rem;
  float: right;
}

.no-data {
  max-height: calc(55vh - 4rem);
  min-height: 523px;
}

.results-box {
  width: 50%;
  height: 100%;
  padding: 5px 5px 5px 21px;
  margin-top: 20px;
}

.results-box-collapsed {
  width: 0;
}

.no-hover:hover {
  background-color: unset;
}

.hide-input {
  display: none !important;
}

.download-button {
  z-index: 10;
  position: absolute;
  bottom: 10px;
  right: 10px;
}

.download-text {
  text-align: right;
  height: 20px;
  margin-left: 10px;
}

.centered {
  text-align: center;
}

.uncentered {
  text-align: unset !important;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.input-wrapper input {
  width: 100%;
}

.input-wrapper .el-select {
  width: 100%;
}

.input-wrapper label {
  font-size: 11px;
  color: $input-label;
  margin-bottom: 2px;
  margin-left: 2px;
}

.button-wrapper {
  flex: 0 0 50%;
  max-width: 50%;
  padding: 0 10px;

  .filter-button {
    height: 48px;
    background-color: $blueberry;
    border-radius: 4px;
    font-family: "Open Sans", sans-serif;
    font-weight: 900;
    font-size: 16px;
    line-height: 30px;
    letter-spacing: 0;
    text-transform: uppercase;
    color: white;
    margin-top: 51px;
    margin-right: 0;
    width: 100%;
    @media screen and (min-width: $break-xl) {
      margin-top: 51px;
      margin-right: 0;
      width: 100%;
    }
    @media screen and (min-width: $break-xxl) {
      margin-top: 51px;
      margin-right: 66px;
      width: 240px;
    }

    transition: background-color 0.2s linear;

    &:hover {
      color: white;
      background-color: #9256d9;
    }
  }
}

.excel-download-button {
  width: 240px;
  height: 48px;
  background-color: $celery-500;
  border-radius: 4px;
  font-family: "Open Sans", sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: $celery-700;
  }
}

.list-filter-wrapper {
  display: flex;
  margin-bottom: 10px;
}

.calculate-confidence-wrapper {
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
}

.select-calculations {
  width: 100%;
}

.fragrance-name {
  min-width: 220px;
  max-width: 220px;
  @media screen and (min-width: $break-xl) {
    min-width: 300px;
    max-width: 300px;
  }
}

.select-all-button {
  margin-right: 10px;
  width: 120px;
  min-width: 120px;
  height: 40px;
  background-color: $blueberry;
  border-radius: 4px;
  font-family: "Open Sans", sans-serif;
  font-weight: 900;
  font-size: 13px;
  line-height: 26px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    background-color: #9256d9;
  }
}

.fragrances-container {
  height: calc(100% - 70px);
  overflow-x: auto;
}

.fragrances-wrapper {
  height: 100%;
  width: 100%;
}

.charts-container {
  width: 100%;
  margin: 20px 0;
  height: 815px;
  overflow-y: auto;
}

.results-header {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  padding: 20px 66px 10px;
  width: 100%;
  border-top: 1px solid #25232c;

  &__title {
    color: #545772;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "Source Sans Pro", sans-serif;
    font-size: 24px;
    text-transform: uppercase;
    line-height: 28px;
    letter-spacing: 0;
    font-weight: 900;
  }
}

.tag {
  background-color: $blueberry;
  border-color: $blueberry;
}

.expand-button {
  margin-left: 10px;
  margin-right: auto;
  width: 120px;
  min-width: 120px;
  height: 40px;
  background-color: $blueberry;
  border-radius: 4px;
  font-family: "Open Sans", sans-serif;
  font-weight: 900;
  font-size: 13px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    background-color: #9256d9;
  }
}
</style>
<style lang="scss">
.checkbox-tooltip {
  left: 30px !important;
}
</style>
